import React, { useMemo, useState } from 'react';
import { Theme } from '../../Enums/Theme.enum';
import { EventMetaKey } from '../../Enums/EventMetaKey.enum';
import { HealthPlanCode } from '../../Enums/HealthPlanCode.enum';
import { PatientTreatmentTabKey } from '../../Enums/PatientTreatmentTabKey.enum';
import { InitialTabParams } from '../../Modules/Patient/Interfaces/InitialTabParams.interface';
import useNotification from '../../Hooks/useNotification.hook';
import {
	acceptCase,
	connectWithPatient,
} from '../../Modules/Case/Services/case.service';
import { useAppContext } from '../../Services/AppContext';
import LinkButton from '../Button/LinkButton';
import ContextMenu from '../ContextMenu/ContextMenu';
import TreatmentModal from '../Treatment/TreatmentModal';
import FullPageLoading from '../Utils/FullPageLoading/FullPageLoading';
import RemoveConfirmationModal from '../RemoveConfirmation/RemoveConfirmationModal';
import useTranslation from '../../Hooks/useTranslation';
import { Moment } from 'moment';
import {
	Meta,
	useDoneEventMutation,
	useLazyPatientProfileDataQuery,
} from '../../graphql/graphql';
import { EventClickArg } from '@fullcalendar/react';
import { getMomentDate } from '../../Utils/dates.util';
import { canConnectWithPatient } from '../../Helpers/Patient/canConnectWithPatient.helper';

interface CalendarContextMenuProps {
	contextParams: object;
	event: EventClickArg;
	onEdit: () => void;
	onRemove: (event: EventClickArg) => void;
	onContextClose: () => void;
	onRemoveCancel: () => void;
	onCreateNewEvent: (date: Moment) => void;
	allowConnectWithPatient?: boolean;
	allowShowPatientProfile?: boolean;
	removeConfirmationText: string;
	removeConfirmationTitle: string;
	onMarkEventDone: () => void;
}

const CalendarContextMenu: React.FC<CalendarContextMenuProps> = ({
	contextParams,
	event,
	onEdit,
	onRemove,
	onContextClose,
	onRemoveCancel,
	onCreateNewEvent,
	allowConnectWithPatient = true,
	allowShowPatientProfile = true,
	removeConfirmationText,
	removeConfirmationTitle,
	onMarkEventDone,
}) => {
	const [treatmentOpen, setTreatmentOpen] = useState(false);
	const [treatmentInitialTabKey, setTreatmentInitialTabKey] =
		useState<PatientTreatmentTabKey>(PatientTreatmentTabKey.PROFILE);
	const [treatmentInitialTabParams, setTreatmentInitialTabParams] = useState<
		InitialTabParams | undefined
	>(undefined);
	const [loading, setLoading] = useState(false);
	const [removeConfirmation, setRemoveConfirmation] = useState(false);
	const eventData = event.event;
	const eventExtendedProps = eventData.extendedProps;
	const patientId = eventExtendedProps.patient.id;
	const loggedUserId = useAppContext().userData?.id;
	const caseId = eventExtendedProps.caseId;
	const meta = eventExtendedProps.meta;
	const workplaceId = eventExtendedProps.workplaceId;
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [markEventDone] = useDoneEventMutation();
	const [getPatientProfile] = useLazyPatientProfileDataQuery();

	const handleConnectWithPatient = async () => {
		try {
			setLoading(true);

			const patientProfile = await getPatientProfile({
				userId: patientId,
			});

			const { enabled, reason } = canConnectWithPatient(patientProfile.data);

			if (!enabled && reason) {
				showNotificationAlert({
					content: reason,
				});
				return;
			}

			if (caseId) {
				await acceptCase(caseId, loggedUserId, true, true);
			} else {
				await connectWithPatient(loggedUserId, patientId, workplaceId);
			}
		} catch (e: any) {
			if (e.response?.data?.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		} finally {
			setLoading(false);
		}
	};

	const handleOpenPatientProfile = () => {
		setTreatmentInitialTabKey(PatientTreatmentTabKey.PROFILE);
		setTreatmentInitialTabParams(undefined);
		setTreatmentOpen(true);
		onContextClose?.();
	};

	const handleOpenDentition = () => {
		setTreatmentInitialTabKey(PatientTreatmentTabKey.HEALTH_PLANS);
		setTreatmentInitialTabParams({
			initialStepPath: HealthPlanCode.DENT,
		});
		setTreatmentOpen(true);
		onContextClose?.();
	};

	const handleEditEvent = () => {
		onEdit?.();
		onContextClose?.();
	};

	const handleOpenRemoveConfirmation = () => {
		setRemoveConfirmation(true);
		onContextClose?.();
	};

	const handleRemoveEvent = () => {
		setRemoveConfirmation(false);
		onRemove?.(event);
		showNotificationSuccess({
			content: t('base.deleted'),
		});
	};

	if (!event) {
		return null;
	}

	const handleCancelRemove = () => {
		setRemoveConfirmation(false);
		onRemoveCancel();
	};

	const handleCreateNewEvent = (date: Date | null) => {
		if (date) {
			onCreateNewEvent(getMomentDate(date));
			onContextClose?.();
		}
	};

	const handleMarkAsDone = (eventId: string) => {
		markEventDone({
			input: {
				eventId: eventId,
			},
		}).unwrap();
		onMarkEventDone();
	};

	const flagDone = useMemo(() => {
		return meta?.some((item: Meta) => item?.key === EventMetaKey.FLAG_DONE);
	}, [meta]);

	const isMeddiDentMedicalExamination = useMemo(() => {
		return meta?.some(
			(item: Meta) =>
				item?.key === EventMetaKey.MEDDIDENT_MEDICAL_EXAMINATION &&
				item?.value === true,
		);
	}, [meta]);

	return (
		<>
			{loading && <FullPageLoading />}
			<ContextMenu
				params={contextParams}
				onContextClose={onContextClose}
				openInModalRoot={true}
				customRootName='context-menu-root'>
				{allowConnectWithPatient ? (
					<>
						<LinkButton
							onClick={() => {
								handleConnectWithPatient();
							}}
							title={'base.shared.contactAPatient'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<div style={{ marginTop: '10px' }} />
					</>
				) : (
					<></>
				)}
				{allowShowPatientProfile ? (
					<>
						<LinkButton
							onClick={() => {
								handleOpenPatientProfile();
							}}
							title={'base.showPatientsCard'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<div style={{ marginTop: '10px' }} />
					</>
				) : (
					<></>
				)}
				{isMeddiDentMedicalExamination && (
					<LinkButton
						onClick={handleOpenDentition}
						title={'patientTreatment.meddiDENT.dentalCross.title'}
						theme={Theme.BLUE}
						firstUpper
					/>
				)}
				<LinkButton
					onClick={() => {
						handleEditEvent();
					}}
					title={'base.shared.edit'}
					theme={Theme.BLUE}
					firstUpper
				/>
				<LinkButton
					onClick={() => handleCreateNewEvent(eventData.start)}
					title={'component.calendarContextMenu.createNewAppointment'}
					theme={Theme.BLUE}
					firstUpper
				/>
				{!flagDone ? (
					<LinkButton
						onClick={() => handleMarkAsDone(eventData.id)}
						title={'component.calendarContextMenu.markAsDone'}
						theme={Theme.BLUE}
						firstUpper
					/>
				) : (
					<></>
				)}
				<div style={{ marginTop: '20px' }} />
				<LinkButton
					title='base.shared.delete'
					theme={Theme.RED}
					onClick={handleOpenRemoveConfirmation}
					firstUpper
				/>
			</ContextMenu>
			<RemoveConfirmationModal
				opened={removeConfirmation}
				onClose={handleCancelRemove}
				onConfirm={handleRemoveEvent}
				confirmationText={removeConfirmationText}
				confirmationTitle={removeConfirmationTitle}
			/>
			<TreatmentModal
				opened={treatmentOpen}
				onClose={() => setTreatmentOpen(false)}
				patientId={patientId}
				workplaceId={workplaceId}
				loggedUserId={loggedUserId}
				caseId={caseId}
				initialTabKey={treatmentInitialTabKey}
				initialTabParams={treatmentInitialTabParams}
			/>
		</>
	);
};

export default CalendarContextMenu;
